import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const authHeaders = (multipart = false) => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  ...(multipart ? {} : { "Content-Type": "application/json" }),
  Lang: i18n.language,
});

const toFormData = (fields) => {
  if (fields instanceof FormData) return fields;
  const formData = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    formData.append(key, value);
  });
  return formData;
};

const buildOrderFilterParams = (filters = {}) => {
  const params = {};
  const keys = [
    "contract_number",
    "order_number",
    "customer_id",
    "customer_type",
    "provider_id",
    "provider_type",
    "working_days_from",
    "working_days_to",
    "status",
    "service_type_id",
    "rental_project_id",
    "rental_type",
  ];

  keys.forEach((key) => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
      params[key] = filters[key];
    }
  });

  if (filters.date_filter) {
    params.date_filter = filters.date_filter;
    if (
      filters.date_filter === "range" &&
      filters.date_value?.[0] &&
      filters.date_value?.[1]
    ) {
      params.date_value = JSON.stringify([
        filters.date_value[0],
        filters.date_value[1],
      ]);
    }
  }

  return params;
};

export const fetchRentalMainOrders = createAsyncThunk(
  "rental_orders_main/fetchAll",
  async ({ page = 1, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/rental-main/orders`, {
        headers: authHeaders(),
        params: { page, ...buildOrderFilterParams(filters) },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

export const addRentalMainOrders = createAsyncThunk(
  "rental_orders_main/add",
  async (userData, { rejectWithValue }) => {
    try {
      const formData = toFormData(userData);
      const response = await axios.post(
        `${BASE_URL}/rental-main/orders`,
        formData,
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);

export const changeStatus = createAsyncThunk(
  "rental_orders_main/changeStatus",
  async ({ status, order_id }, { rejectWithValue }) => {
    try {
      const formData = toFormData({ status, order_id });
      const response = await axios.post(
        `${BASE_URL}/rental-main/orders/update-status`,
        formData,
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      const backendMessage = error.response?.data?.message;
      const message =
        backendMessage ||
        error.response?.data?.errors?.order_id?.[0] ||
        "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);

export const exportRentalMainOrders = createAsyncThunk(
  "rental_orders_main/export",
  async ({ exportOptions = {}, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file_type", exportOptions.file_type || "excel");
      formData.append("language", exportOptions.language || i18n.language);
      formData.append(
        "table_direction",
        exportOptions.table_direction ||
          (i18n.language === "ar" ? "rtl" : "ltr")
      );

      Object.entries(exportOptions.columns || {}).forEach(([key, val]) => {
        if (val === 1) formData.append(`columns[${key}]`, "1");
      });

      const filterParams = buildOrderFilterParams(filters);
      Object.entries(filterParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await axios.post(
        `${BASE_URL}/rental-main/orders/export`,
        formData,
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);

const RentalMainOrdersSlice = createSlice({
  name: "rental_orders_main",
  initialState: {
    rental_orders_main: [],
    record: null,
    isLoading: false,
    isExporting: false,
    error: null,
    success: null,
    successDelete: null,
    successChangeStatus: null,
    errorChangeStatus: null,
    errorDelete: null,
    exportSuccess: null,
    exportError: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.successChangeStatus = null;
      state.errorChangeStatus = null;
      state.exportSuccess = null;
      state.exportError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRentalMainOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRentalMainOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rental_orders_main = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchRentalMainOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(addRentalMainOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRentalMainOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addRentalMainOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(changeStatus.pending, (state) => {
        state.isLoading = true;
        state.errorChangeStatus = null;
      })
      .addCase(changeStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successChangeStatus = action.payload.message;
      })
      .addCase(changeStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.errorChangeStatus = action.payload || "Failed to update status";
      })
      .addCase(exportRentalMainOrders.pending, (state) => {
        state.isExporting = true;
        state.exportError = null;
        state.exportSuccess = null;
      })
      .addCase(exportRentalMainOrders.fulfilled, (state, action) => {
        state.isExporting = false;
        state.exportSuccess =
          action.payload?.message ||
          (Array.isArray(action.payload?.data) &&
            action.payload.data[0]?.original?.message) ||
          "Exported successfully";
      })
      .addCase(exportRentalMainOrders.rejected, (state, action) => {
        state.isExporting = false;
        state.exportError = action.payload || "Failed to export";
      });
  },
});

export const { clearState } = RentalMainOrdersSlice.actions;
export default RentalMainOrdersSlice.reducer;
